import React, { Component, RefObject } from 'react';
import { X, Image, Video, FileText, Upload, Eye, Tag, Plus } from 'lucide-react';
import styles from './PostCreateForm.module.scss';

interface FilePreview {
  file: File;
  preview: string;
  type: 'image' | 'video' | 'gif' | 'file';
}

interface PostCreateFormProps {
  onSubmit: (data: {
    title?: string;
    description?: string;
    contentText?: string;
    status: 'draft' | 'published';
    files: File[];
    tags?: string[];
  }) => Promise<void>;
  onCancel?: () => void;
  initialData?: {
    title?: string;
    description?: string;
    contentText?: string;
    status?: 'draft' | 'published';
    tags?: string[];
  };
  isEditing?: boolean;
}

interface PostCreateFormState {
  title: string;
  description: string;
  contentText: string;
  status: 'draft' | 'published';
  tags: string[];
  newTag: string;
  files: File[];
  filePreviews: FilePreview[];
  isSubmitting: boolean;
  errors: {
    title?: string;
    description?: string;
    contentText?: string;
    files?: string;
    tags?: string;
  };
}

export class PostCreateForm extends Component<PostCreateFormProps, PostCreateFormState> {
  private fileInputRef: RefObject<HTMLInputElement> = React.createRef();

  private tagInputRef: RefObject<HTMLInputElement> = React.createRef();

  constructor(props: PostCreateFormProps) {
    super(props);
    this.state = {
      title: props.initialData?.title || '',
      description: props.initialData?.description || '',
      contentText: props.initialData?.contentText || '',
      status: props.initialData?.status || 'draft',
      tags: props.initialData?.tags || [],
      newTag: '',
      files: [],
      filePreviews: [],
      isSubmitting: false,
      errors: {},
    };
  }

  handleInputChange = (field: 'title' | 'description' | 'contentText', value: string) => {
    this.setState({
      [field]: value,
      errors: { ...this.state.errors, [field]: undefined },
    } as any);
  };

  handleStatusChange = (status: 'draft' | 'published') => {
    this.setState({ status });
  };

  handleAddTag = (e: React.FormEvent) => {
    e.preventDefault();
    const tag = this.state.newTag.trim();
    
    if (!tag) return;
    
    // Validate tag length
    if (tag.length > 50) {
      this.setState({
        errors: {
          ...this.state.errors,
          tags: 'Tags must be 50 characters or less',
        },
      });
      return;
    }

    // Check for duplicates (case-insensitive)
    const tagLower = tag.toLowerCase();
    if (this.state.tags.some(t => t.toLowerCase() === tagLower)) {
      this.setState({
        errors: {
          ...this.state.errors,
          tags: 'Tag already exists',
        },
      });
      return;
    }

    // Validate max tags
    if (this.state.tags.length >= 10) {
      this.setState({
        errors: {
          ...this.state.errors,
          tags: 'Maximum 10 tags allowed',
        },
      });
      return;
    }

    // Add tag
    this.setState({
      tags: [...this.state.tags, tag],
      newTag: '',
      errors: { ...this.state.errors, tags: undefined },
    });
  };

  handleRemoveTag = (index: number) => {
    const newTags = [...this.state.tags];
    newTags.splice(index, 1);
    this.setState({ tags: newTags });
  };

  handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleAddTag(e);
    } else if (e.key === 'Backspace' && this.state.newTag === '' && this.state.tags.length > 0) {
      // Remove last tag if backspace is pressed on empty input
      this.handleRemoveTag(this.state.tags.length - 1);
    }
  };

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // Validate file count
    const totalFiles = this.state.files.length + selectedFiles.length;
    if (totalFiles > 10) {
      this.setState({
        errors: {
          ...this.state.errors,
          files: 'Maximum 10 files allowed',
        },
      });
      return;
    }

    // Validate file sizes (max 50MB per file)
    const maxSize = 50 * 1024 * 1024; // 50MB
    const oversizedFiles = selectedFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      this.setState({
        errors: {
          ...this.state.errors,
          files: 'Some files exceed 50MB limit',
        },
      });
      return;
    }

    // Create previews
    const newPreviews: FilePreview[] = [];
    selectedFiles.forEach(file => {
      const type = this.getFileType(file);
      const preview: FilePreview = {
        file,
        preview: '',
        type,
      };

      if (type === 'image' || type === 'gif') {
        const reader = new FileReader();
        reader.onload = (e) => {
          preview.preview = e.target?.result as string;
          this.forceUpdate();
        };
        reader.readAsDataURL(file);
      } else if (type === 'video') {
        const reader = new FileReader();
        reader.onload = (e) => {
          preview.preview = e.target?.result as string;
          this.forceUpdate();
        };
        reader.readAsDataURL(file);
      }

      newPreviews.push(preview);
    });

    this.setState({
      files: [...this.state.files, ...selectedFiles],
      filePreviews: [...this.state.filePreviews, ...newPreviews],
      errors: { ...this.state.errors, files: undefined },
    });
  };

  getFileType = (file: File): 'image' | 'video' | 'gif' | 'file' => {
    if (file.type.startsWith('image/')) {
      if (file.type === 'image/gif') return 'gif';
      return 'image';
    }
    if (file.type.startsWith('video/')) return 'video';
    return 'file';
  };

  removeFile = (index: number) => {
    const newFiles = [...this.state.files];
    const newPreviews = [...this.state.filePreviews];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    this.setState({
      files: newFiles,
      filePreviews: newPreviews,
    });
  };

  validate = (): boolean => {
    const errors: any = {};

    // Title is mandatory
    if (!this.state.title.trim()) {
      errors.title = 'Title is required';
    } else if (this.state.title.length > 255) {
      errors.title = 'Title must be 255 characters or less';
    }

    // At least one of contentText or files must be provided
    if (!this.state.contentText.trim() && this.state.files.length === 0) {
      errors.contentText = 'Post content or files are required';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!this.validate()) return;

    this.setState({ isSubmitting: true });

    try {
      await this.props.onSubmit({
        title: this.state.title.trim() || undefined,
        description: this.state.description.trim() || undefined,
        contentText: this.state.contentText.trim() || undefined,
        status: this.state.status,
        files: this.state.files,
        tags: this.state.tags.length > 0 ? this.state.tags : undefined,
      });
    } catch (error) {
      console.error('Failed to submit post:', error);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  render() {
    const {
      title,
      description,
      contentText,
      status,
      tags,
      files,
      filePreviews,
      isSubmitting,
      errors,
    } = this.state;

    return (
      <form className={styles.postForm} onSubmit={this.handleSubmit}>
        <div className={styles.formHeader}>
          <h2 className={styles.formTitle}>
            {this.props.isEditing ? 'Edit Post' : 'Create New Post'}
          </h2>
          {this.props.onCancel && (
            <button
              type="button"
              className={styles.cancelButton}
              onClick={this.props.onCancel}
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className={styles.formBody}>
          {/* Title */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Title <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={`${styles.input} ${errors.title ? styles.error : ''}`}
              placeholder="Enter post title..."
              value={title}
              onChange={(e) => this.handleInputChange('title', e.target.value)}
              maxLength={255}
              required
            />
            {errors.title && (
              <span className={styles.errorMessage}>{errors.title}</span>
            )}
          </div>

          {/* Description */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Description <span className={styles.optional}>(optional)</span>
            </label>
            <textarea
              className={`${styles.textarea} ${errors.description ? styles.error : ''}`}
              placeholder="Enter post description..."
              value={description}
              onChange={(e) => this.handleInputChange('description', e.target.value)}
              rows={3}
            />
            {errors.description && (
              <span className={styles.errorMessage}>{errors.description}</span>
            )}
          </div>

          {/* Content Text */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Content <span className={styles.optional}>(optional if files are attached)</span>
            </label>
            <textarea
              className={`${styles.textarea} ${errors.contentText ? styles.error : ''}`}
              placeholder="What's on your mind?"
              value={contentText}
              onChange={(e) => this.handleInputChange('contentText', e.target.value)}
              rows={6}
            />
            {errors.contentText && (
              <span className={styles.errorMessage}>{errors.contentText}</span>
            )}
          </div>

          {/* Tags */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Tags/Categories <span className={styles.optional}>(optional, max 10 tags)</span>
            </label>
            <div className={styles.tagsContainer}>
              {/* Existing Tags */}
              {this.state.tags.length > 0 && (
                <div className={styles.tagsList}>
                  {this.state.tags.map((tag, index) => (
                    <div key={index} className={styles.tagItem}>
                      <span className={styles.tagText}>{tag}</span>
                      <button
                        type="button"
                        className={styles.removeTagButton}
                        onClick={() => this.handleRemoveTag(index)}
                        title="Remove tag"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Tag Input */}
              <form onSubmit={this.handleAddTag} className={styles.tagInputForm}>
                <div className={styles.tagInputWrapper}>
                  <Tag size={18} className={styles.tagIcon} />
                  <input
                    ref={this.tagInputRef}
                    type="text"
                    className={styles.tagInput}
                    placeholder="Enter a tag (e.g., Music, Rock, Classic)..."
                    value={this.state.newTag}
                    onChange={(e) => this.setState({ newTag: e.target.value, errors: { ...this.state.errors, tags: undefined } })}
                    onKeyDown={this.handleTagInputKeyDown}
                    maxLength={50}
                  />
                  <button
                    type="submit"
                    className={styles.addTagButton}
                    disabled={!this.state.newTag.trim()}
                    title="Add tag"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </form>
              {errors.tags && (
                <span className={styles.errorMessage}>{errors.tags}</span>
              )}
              <p className={styles.tagsHint}>
                Add tags to categorize your post (e.g., Music, Rock, Classic, Sports, etc.)
              </p>
            </div>
          </div>

          {/* File Upload */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Attachments <span className={styles.optional}>(optional, max 10 files, 50MB each)</span>
            </label>
            <div className={styles.fileUploadArea}>
              <input
                ref={this.fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx"
                onChange={this.handleFileSelect}
                className={styles.fileInput}
              />
              <button
                type="button"
                className={styles.uploadButton}
                onClick={() => this.fileInputRef.current?.click()}
              >
                <Upload size={20} />
                <span>Choose Files</span>
              </button>
              <p className={styles.uploadHint}>
                Supported: Images, Videos, GIFs, PDFs, Word documents
              </p>
            </div>
            {errors.files && (
              <span className={styles.errorMessage}>{errors.files}</span>
            )}

            {/* File Previews */}
            {filePreviews.length > 0 && (
              <div className={styles.filePreviews}>
                {filePreviews.map((preview, index) => (
                  <div key={index} className={styles.filePreview}>
                    {preview.type === 'image' || preview.type === 'gif' ? (
                      <img
                        src={preview.preview}
                        alt={preview.file.name}
                        className={styles.previewImage}
                      />
                    ) : preview.type === 'video' ? (
                      <video
                        src={preview.preview}
                        className={styles.previewVideo}
                        controls
                      />
                    ) : (
                      <div className={styles.previewFile}>
                        <FileText size={32} />
                        <span>{preview.file.name}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.removeFileButton}
                      onClick={() => this.removeFile(index)}
                      title="Remove file"
                    >
                      <X size={16} />
                    </button>
                    <div className={styles.fileInfo}>
                      <span className={styles.fileName}>{preview.file.name}</span>
                      <span className={styles.fileSize}>
                        {this.formatFileSize(preview.file.size)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Status</label>
            <div className={styles.statusButtons}>
              <button
                type="button"
                className={`${styles.statusButton} ${status === 'draft' ? styles.active : ''}`}
                onClick={() => this.handleStatusChange('draft')}
              >
                <FileText size={18} />
                <span>Save as Draft</span>
              </button>
              <button
                type="button"
                className={`${styles.statusButton} ${status === 'published' ? styles.active : ''}`}
                onClick={() => this.handleStatusChange('published')}
              >
                <Eye size={18} />
                <span>Publish Now</span>
              </button>
            </div>
          </div>
        </div>

        <div className={styles.formFooter}>
          {this.props.onCancel && (
            <button
              type="button"
              className={styles.cancelFooterButton}
              onClick={this.props.onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : this.props.isEditing ? 'Update Post' : 'Create Post'}
          </button>
        </div>
      </form>
    );
  }
}

export default PostCreateForm;

